import React, { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, Zap, IndianRupee, Gift, Bike, Package, CalendarCheck,
  Wallet, MapPin, FileBarChart, UserPlus, Settings, ScrollText, Sparkles, Moon, Sun,
  Bell, Menu, ChevronLeft, Lock, LogOut, CheckCheck, Building2, Network, ArrowLeftRight, ShieldCheck, MessageCircle, Receipt, Award,
} from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { GlobalSearch } from "@/components/GlobalSearch";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

const REP = ["fr.reports", "pay.history", ...["daily", "weekly", "monthly", "quarterly", "yearly", "custom"].map((p) => `rep.view_${p}`)];
export const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, perms: ["dash.view", "dash.financial", "dash.analytics"] },
  { to: "/my-franchise", label: "My Franchise", icon: Network, fo: true },
  { to: "/partners", label: "Delivery Partners", foLabel: "My Delivery Partners", icon: Users, perms: ["dp.view"] },
  { to: "/franchise", label: "Franchise Partners", icon: Building2, perms: ["fr.view"], noFo: true },
  { to: "/transfers", label: "Transfer History", icon: ArrowLeftRight, perms: ["transfer.view"] },
  { to: "/franchise-dashboard", label: "Franchise Dashboard", icon: Network, perms: ["fr.reports"], noFo: true },
  { to: "/operations", label: "Daily Operations", foLabel: "My Orders", icon: Zap, perms: ["daily.add", "daily.edit", "dp.view"] },
  { to: "/earnings", label: "Earnings", icon: IndianRupee, perms: ["dp.earnings", "dash.analytics"], noFo: true },
  { to: "/weekly-send", label: "Weekly Send", icon: MessageCircle, perms: ["dp.earnings"] },
  { to: "/incentives", label: "Incentives", icon: Gift, perms: ["incentive.add"] },
  { to: "/ev", label: "EV Tym", icon: Bike, perms: ["mod.ev"] },
  { to: "/assets", label: "Assets", icon: Package, perms: ["mod.assets"] },
  { to: "/attendance", label: "Attendance", icon: CalendarCheck, perms: ["mod.attendance"] },
  { to: "/payouts", label: "Payouts", icon: Wallet, perms: ["mod.payouts"] },
  { to: "/payments", label: "Payments", icon: IndianRupee, perms: ["pay.view_withdrawals", "pay.history"] },
  { to: "/tds", label: "TDS Management", foLabel: "My Commission & TDS", icon: Receipt, perms: ["tax.view", "fr.payout_view", "fr.payout_create", "fr.payout_pay"] },
  { to: "/certificates", label: "Certificates", foLabel: "My Certificates", icon: Award, perms: ["cert.view"] },
  { to: "/month-close", label: "Month Close", icon: Lock, perms: ["mod.month_close"] },
  { to: "/locations", label: "Locations", icon: MapPin, perms: ["set.locations"] },
  { to: "/reports", label: "Reports", foLabel: "My Reports", icon: FileBarChart, perms: REP },
  { to: "/onboarding", label: "Onboarding", icon: UserPlus, perms: ["mod.onboarding"] },
  { to: "/settings", label: "Settings", icon: Settings, perms: ["set.view_models", "mod.settings", "set.multi_rate", "fr.commission_view", "pen.settings", "tax.settings", "tax.view", "daily.penalty", "cert.branding"], noFo: true },
  { to: "/audit", label: "Audit Logs", icon: ScrollText, superOnly: true },
  { to: "/admin/users", label: "User & Role Management", icon: ShieldCheck, superOnly: true },
];

export const visibleNav = (user, canAny) => {
  const isFo = user?.role === "Franchise Owner";
  return NAV.filter((n) => {
    if (n.superOnly) return !!user?.is_super;
    if (n.fo) return isFo;
    if (isFo && n.noFo) return false;
    return canAny(...n.perms);
  }).map((n) => ({ ...n, label: isFo && n.foLabel ? n.foLabel : n.label }));
};

const LEVEL_DOT = { warning: "bg-amber-500", success: "bg-emerald-500", info: "bg-blue-500", error: "bg-rose-500" };

export function Layout({ children }) {
  const { hub, setHub, theme, toggleTheme, setInsightsOpen } = useApp();
  const { user, logout, canAny } = useAuth();
  const nav = visibleNav(user, canAny);
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [feed, setFeed] = useState({ items: [], unread: 0 });
  const [locs, setLocs] = useState([]);
  const [bellOpen, setBellOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);

  const loadNotifs = () => api.get("/notifications").then(({ data }) => setFeed(data)).catch(() => {});
  useEffect(() => { loadNotifs(); api.get("/locations").then(({ data }) => setLocs(data)).catch(() => {}); }, []);
  const fullAccess = !user?.restricted;

  const markAll = async () => { await api.post("/notifications/read-all"); loadNotifs(); };
  const initials = (n) => (n || "U").split(" ").map((x) => x[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="flex min-h-screen bg-background">
      <aside className={cn(
        "fixed inset-y-0 left-0 z-50 flex flex-col bg-[hsl(var(--sidebar))] text-slate-300 transition-all duration-300 lg:static",
        collapsed ? "w-20" : "w-64",
        mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0")}>
        <div className="flex h-16 items-center gap-2.5 border-b border-white/10 px-5">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500 to-emerald-500 font-head text-lg font-black text-white">C</div>
          {!collapsed && <div className="leading-tight"><p className="font-head text-base font-bold text-white">Connect Tym</p><p className="text-[10px] uppercase tracking-widest text-slate-400">EV Tym CRM</p></div>}
        </div>
        <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-4">
          {nav.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setMobileOpen(false)}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) => cn(
                "group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
                isActive ? "bg-blue-500/15 text-white shadow-[inset_2px_0_0] shadow-blue-400" : "text-slate-400 hover:bg-white/5 hover:text-white")}>
              <n.icon className="h-[18px] w-[18px] shrink-0" />
              {!collapsed && <span className="truncate">{n.label}</span>}
            </NavLink>
          ))}
        </nav>
        <button onClick={() => setCollapsed((c) => !c)} className="hidden items-center justify-center gap-2 border-t border-white/10 py-3 text-xs text-slate-400 hover:text-white lg:flex">
          <ChevronLeft className={cn("h-4 w-4 transition-transform", collapsed && "rotate-180")} /> {!collapsed && "Collapse"}
        </button>
      </aside>

      {mobileOpen && <div className="fixed inset-0 z-40 bg-black/50 lg:hidden" onClick={() => setMobileOpen(false)} />}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-40 flex h-16 items-center justify-between gap-3 border-b border-border bg-background/80 px-4 backdrop-blur-md sm:px-6">
          <div className="flex flex-1 items-center gap-3">
            <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn"><Menu className="h-5 w-5" /></Button>
            <GlobalSearch />
          </div>
          <div className="flex items-center gap-2">
            {fullAccess && <Select value={hub} onValueChange={setHub}>
              <SelectTrigger className="hidden h-9 w-36 sm:flex" data-testid="hub-location-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="All">All Hubs</SelectItem>
                {locs.map((l) => <SelectItem key={l.name} value={l.name}>{l.name}</SelectItem>)}
              </SelectContent>
            </Select>}

            {user?.is_super && <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => setInsightsOpen(true)} data-testid="ai-insights-toggle-btn" title="AI Smart Insights">
              <Sparkles className="h-[18px] w-[18px] text-violet-500" />
            </Button>}

            {/* Notifications */}
            <div className="relative">
              <Button variant="ghost" size="icon" className="h-9 w-9" data-testid="notifications-btn" onClick={() => { setBellOpen((o) => !o); setUserOpen(false); }} title={`${feed.unread} unread`}><Bell className="h-[18px] w-[18px]" /></Button>
              {feed.unread > 0 && <span className="pointer-events-none absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-bold text-white" data-testid="notif-badge">{feed.unread}</span>}
              {bellOpen && <>
                <div className="fixed inset-0 z-40" onClick={() => setBellOpen(false)} />
                <div className="absolute right-0 top-11 z-50 w-80 overflow-hidden rounded-xl border border-border bg-popover shadow-2xl" data-testid="notifications-dropdown">
                  <div className="flex items-center justify-between border-b border-border px-4 py-3">
                    <p className="font-head text-sm font-semibold">Notifications</p>
                    <button onClick={markAll} className="flex items-center gap-1 text-xs text-blue-500 hover:underline" data-testid="notif-mark-all"><CheckCheck className="h-3.5 w-3.5" /> Mark all read</button>
                  </div>
                  <div className="max-h-96 overflow-y-auto">
                    {feed.items.length === 0 && <p className="px-4 py-6 text-center text-sm text-muted-foreground">No notifications</p>}
                    {feed.items.map((n) => (
                      <div key={n.id} data-testid={`notif-${n.id}`} className={cn("flex gap-3 border-b border-border px-4 py-3", !n.read && "bg-primary/5")}>
                        <span className={cn("mt-1.5 h-2 w-2 shrink-0 rounded-full", LEVEL_DOT[n.level] || "bg-slate-400")} />
                        <div className="min-w-0">
                          <p className="text-sm font-medium">{n.title} <span className="ml-1 rounded bg-secondary px-1.5 py-0.5 text-[10px] text-muted-foreground">{n.category}</span></p>
                          <p className="text-xs text-muted-foreground">{n.message}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>}
            </div>

            <Button variant="ghost" size="icon" className="h-9 w-9" onClick={toggleTheme} data-testid="dark-mode-toggle-btn">
              {theme === "dark" ? <Sun className="h-[18px] w-[18px]" /> : <Moon className="h-[18px] w-[18px]" />}
            </Button>

            {/* User menu */}
            <div className="relative">
              <button onClick={() => { setUserOpen((o) => !o); setBellOpen(false); }} data-testid="user-menu-btn" className="flex items-center gap-2 rounded-lg border border-border py-1 pl-1 pr-2.5 hover:bg-accent">
                <span className="flex h-7 w-7 items-center justify-center rounded-md bg-gradient-to-br from-blue-500 to-emerald-500 text-[11px] font-bold text-white">{initials(user?.name)}</span>
                <span className="hidden text-left leading-tight md:block"><span className="block text-xs font-semibold">{user?.name}</span><span className="block text-[10px] text-muted-foreground">{user?.role}</span></span>
              </button>
              {userOpen && <>
                <div className="fixed inset-0 z-40" onClick={() => setUserOpen(false)} />
                <div className="absolute right-0 top-11 z-50 w-56 overflow-hidden rounded-xl border border-border bg-popover shadow-2xl" data-testid="user-dropdown">
                  <div className="border-b border-border px-4 py-3"><p className="text-sm font-semibold">{user?.name}</p><p className="text-xs text-muted-foreground">{user?.email || user?.login_id}</p><p className="mt-1 inline-block rounded bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">{user?.role}</p></div>
                  <button onClick={logout} data-testid="logout-btn" className="flex w-full items-center gap-2 px-4 py-3 text-sm text-rose-500 hover:bg-accent"><LogOut className="h-4 w-4" /> Sign out</button>
                </div>
              </>}
            </div>
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8">{children}</main>
      </div>
    </div>
  );
}
